export type WebGameProjectImageSource = {
    coverImageUrl?: string | null;
    metadata?: Record<string, unknown> | null;
};

const METADATA_IMAGE_KEYS = new Set([
    'coverImageUrl',
    'imageUrl',
    'iconUrl',
    'backgroundUrl',
    'photoUrl',
    'imgUrl',
]);

const IMAGE_PATH_PATTERN = /\.(png|jpe?g|webp|gif|svg)(\?.*)?$/i;

export function looksLikeWebGameImagePath(value: string): boolean {
    const normalized = value.trim();
    if (!normalized) return false;
    if (normalized.startsWith('http://') || normalized.startsWith('https://')) {
        return IMAGE_PATH_PATTERN.test(normalized);
    }
    if (IMAGE_PATH_PATTERN.test(normalized)) return true;
    return (
        normalized.includes('/covers/')
        || normalized.includes('/uploads_api/')
        || normalized.includes('/uploads/')
        || normalized.includes('/webgames/assets/')
        || normalized.startsWith('/webgames/covers/')
        || normalized.startsWith('webgames/covers/')
        || normalized.startsWith('uploads_api/')
    );
}

function addProjectImageUrl(sink: Set<string>, raw: string | null | undefined): void {
    const normalized = raw?.trim();
    if (!normalized) return;
    sink.add(normalized);
}

function collectImageUrlsFromMetadata(metadata: Record<string, unknown>, sink: Set<string>, depth = 0): void {
    if (depth > 8) return;
    for (const [key, value] of Object.entries(metadata)) {
        if (typeof value === 'string') {
            const normalized = value.trim();
            if (!normalized) continue;
            if (METADATA_IMAGE_KEYS.has(key) || looksLikeWebGameImagePath(normalized)) {
                sink.add(normalized);
            }
            continue;
        }
        if (Array.isArray(value)) {
            value.forEach(item => collectImageUrlsFromUnknown(item, sink, depth + 1));
            continue;
        }
        if (value != null && typeof value === 'object') {
            collectImageUrlsFromMetadata(value as Record<string, unknown>, sink, depth + 1);
        }
    }
}

function collectImageUrlsFromUnknown(value: unknown, sink: Set<string>, depth = 0): void {
    if (depth > 8) return;
    if (typeof value === 'string') {
        const normalized = value.trim();
        if (normalized && looksLikeWebGameImagePath(normalized)) {
            sink.add(normalized);
        }
        return;
    }
    if (Array.isArray(value)) {
        value.forEach(item => collectImageUrlsFromUnknown(item, sink, depth + 1));
        return;
    }
    if (value != null && typeof value === 'object') {
        collectImageUrlsFromMetadata(value as Record<string, unknown>, sink, depth + 1);
    }
}

export function collectWebGameProjectImageUrls(sources: ReadonlyArray<WebGameProjectImageSource>): string[] {
    const urls = new Set<string>();
    sources.forEach(source => {
        addProjectImageUrl(urls, source.coverImageUrl);
        if (source.metadata) {
            collectImageUrlsFromMetadata(source.metadata, urls);
        }
    });
    return Array.from(urls).sort((left, right) => left.localeCompare(right));
}
